import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChatCircleDots, PaperPlaneTilt, ArrowUUpLeft, Prohibit, PhoneX, CircleNotch,
  Trash, DownloadSimple, WarningCircle,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";

// bucket key, label, accent color
const BUCKETS = [
  ["follow_up", "Follow-up Required", "#B45309"],
  ["won", "Won", "#16A34A"],
  ["qualified", "Qualified", "#2563EB"],
  ["unqualified", "Unqualified", "#71717A"],
];
const BUCKET_LABEL = Object.fromEntries(BUCKETS.map(([k, l]) => [k, l]));
const TABS = [["all", "All"], ...BUCKETS.map(([k, l]) => [k, l]), ["dnc", "Do Not Call"]];
const CLASSIFY_OPTS = [
  ["positive", "Positive", "#16A34A"],
  ["negative", "Negative", "#DC2626"],
  ["neutral", "Neutral", "#71717A"],
  ["wrong_person", "Wrong Person", "#B45309"],
];

const CLS_STYLE = {
  hard_reject: { color: "#DC2626", label: "HARD REJECT" },
  positive: { color: "#16A34A", label: "POSITIVE" },
  negative: { color: "#DC2626", label: "NEGATIVE" },
  neutral: { color: "#71717A", label: "NEUTRAL" },
  wrong_person: { color: "#B45309", label: "WRONG PERSON" },
  opt_out: { color: "#DC2626", label: "OPT-OUT" },
};

function ClsPill({ cls, confidence, testid }) {
  if (!cls) {
    return (
      <span data-testid={testid} className="font-mono text-[10px] uppercase tracking-wider text-sv-muted flex items-center gap-1">
        <CircleNotch size={11} weight="bold" className="animate-spin" /> classifying
      </span>
    );
  }
  const s = CLS_STYLE[cls] || CLS_STYLE.neutral;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 border font-mono text-[10px] uppercase tracking-wider"
      style={{ color: s.color, borderColor: s.color + "55", background: s.color + "12" }}
    >
      {s.label}
      {typeof confidence === "number" && confidence > 0 ? ` ${Math.round(confidence * 100)}%` : ""}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Conversations() {
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [list, setList] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(params.get("contact") || null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // { message, onConfirm }
  const [exporting, setExporting] = useState(false);
  const bottomRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([
        api.get("/conversations", { params: { filter } }),
        api.get("/conversations/counts"),
      ]);
      setList(l.data);
      setCounts(c.data);
    } catch (e) {
      // transient
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadList();
    const t = setInterval(loadList, 4000);
    return () => clearInterval(t);
  }, [loadList]);

  const loadThread = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const { data } = await api.get(`/conversations/${cid}`);
      setThread(data);
    } catch (e) {
      if (e.response?.status === 404) { setThread(null); setSelectedId(null); }
    }
  }, []);

  useEffect(() => { if (selectedId) loadThread(selectedId); }, [selectedId, loadThread]);
  useEffect(() => {
    if (!selectedId) return;
    const t = setInterval(() => loadThread(selectedId), 4000);
    return () => clearInterval(t);
  }, [selectedId, loadThread]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread?.thread?.length]);

  const select = (cid) => {
    setSelectedId(cid);
    setReply("");
    setParams({ contact: cid }, { replace: true });
  };

  const changeFilter = (val) => {
    setFilter(val);
    setSelectedId(null);
    setThread(null);
    setChecked(new Set());
    setParams({}, { replace: true });
  };

  const toggleCheck = (cid, e) => {
    e.stopPropagation();
    setChecked((prev) => {
      const n = new Set(prev);
      n.has(cid) ? n.delete(cid) : n.add(cid);
      return n;
    });
  };

  const sendReply = async () => {
    if (!reply.trim() || !selectedId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${selectedId}/reply`, { message: reply.trim() });
      setThread((t) => ({ ...t, thread: data.thread }));
      setReply("");
      toast.success("Reply sent");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const undoOptout = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/contacts/${selectedId}/undo-optout`);
      toast.success("Opt-out reversed");
      loadThread(selectedId);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const moveBucket = async (bucket) => {
    if (!selectedId) return;
    try {
      await api.post(`/conversations/${selectedId}/bucket`, { bucket });
      setThread((t) => ({ ...t, bucket, do_not_call: false, opt_out: false, needs_attention: false }));
      toast.success(`Moved to ${BUCKET_LABEL[bucket]}`);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const setClassification = async (classification) => {
    if (!selectedId) return;
    try {
      await api.post(`/conversations/${selectedId}/classify`, { classification });
      setThread((t) => ({ ...t, classification, confidence: 1 }));
      toast.success(`Tagged as ${classification.replace("_", " ")}`);
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const setDncOn = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/conversations/${selectedId}/dnc`, { on: true });
      setThread((t) => ({ ...t, do_not_call: true, needs_attention: false }));
      toast.success("Moved to Do Not Call — campaign stopped");
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const onBucketSelect = (val) => {
    if (val === "dnc") setDncOn();
    else moveBucket(val);
  };

  const runDelete = async (ids) => {
    try {
      if (ids.length === 1) await api.delete(`/conversations/${ids[0]}`);
      else await api.post("/conversations/delete", { ids });
      toast.success(`Deleted ${ids.length} conversation${ids.length === 1 ? "" : "s"}`);
      if (ids.includes(selectedId)) { setSelectedId(null); setThread(null); }
      setChecked(new Set());
      loadList();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setConfirm(null);
    }
  };

  const askBulkDelete = () => {
    const ids = Array.from(checked);
    if (!ids.length) return;
    setConfirm({
      message: `Delete ${ids.length} selected conversation${ids.length === 1 ? "" : "s"}? This cannot be undone.`,
      onConfirm: () => runDelete(ids),
    });
  };

  const askThreadDelete = () => {
    if (!selectedId) return;
    setConfirm({
      message: "Delete this conversation? This cannot be undone.",
      onConfirm: () => runDelete([selectedId]),
    });
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/conversations/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "conversations.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Conversations exported");
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between border-b border-sv-border pb-6">
        <div>
          <p className="font-mono text-xs text-sv-muted uppercase tracking-widest">// inbox</p>
          <h1 className="font-display font-black text-4xl tracking-tight uppercase mt-1">Conversations</h1>
        </div>
        <button
          data-testid="export-conversations-button"
          onClick={exportCsv}
          disabled={exporting}
          className="flex items-center gap-2 h-10 px-4 border border-sv-border text-sm hover:bg-sv-hover transition-colors duration-150 disabled:opacity-50"
        >
          <DownloadSimple size={16} weight="bold" /> {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {/* Bucket tabs */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="conversation-filters">
        {TABS.map(([val, label]) => (
          <button
            key={val}
            data-testid={`filter-${val}`}
            onClick={() => changeFilter(val)}
            className={`h-9 px-4 font-mono text-xs uppercase tracking-wider border transition-colors duration-150 flex items-center gap-2 ${
              filter === val
                ? "bg-sv-ink text-sv-onink border-sv-ink"
                : "bg-transparent text-sv-secondary border-sv-border hover:bg-sv-hover hover:text-sv-ink"
            }`}
          >
            {label}
            <span className={`font-mono text-[10px] ${filter === val ? "text-sv-onink/70" : "text-sv-muted"}`}>
              {counts[val] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      {checked.size > 0 && (
        <div
          data-testid="bulk-actions-bar"
          className="mt-3 flex items-center justify-between border border-sv-ink bg-sv-ink text-sv-onink px-4 py-2"
        >
          <span className="font-mono text-xs uppercase tracking-wider" data-testid="bulk-selected-count">
            {checked.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="bulk-clear-button"
              onClick={() => setChecked(new Set())}
              className="h-8 px-3 font-mono text-[11px] uppercase tracking-wider border border-sv-onink/40 hover:bg-sv-onink/10 transition-colors duration-150"
            >
              Clear
            </button>
            <button
              data-testid="delete-selected-button"
              onClick={askBulkDelete}
              className="h-8 px-3 font-mono text-[11px] uppercase tracking-wider flex items-center gap-1.5"
              style={{ background: "#DC2626", color: "#fff" }}
            >
              <Trash size={13} weight="bold" /> Delete selected
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4">
        {/* List */}
        <div className="border border-sv-border bg-sv-surface max-h-[70vh] overflow-y-auto" data-testid="conversation-list">
          {loading ? (
            <p className="font-mono text-sm text-sv-muted p-6">LOADING…</p>
          ) : list.length === 0 ? (
            <div className="p-10 text-center">
              <p className="font-mono text-sm text-sv-muted">NO_CONVERSATIONS</p>
              <p className="text-sv-secondary text-sm mt-2">Replies matching this bucket will appear here.</p>
            </div>
          ) : (
            list.map((c) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                data-testid={`conversation-item-${c.id}`}
                onClick={() => select(c.id)}
                onKeyDown={(e) => { if (e.key === "Enter") select(c.id); }}
                className={`w-full text-left px-4 py-3 border-b border-sv-border last:border-0 transition-colors duration-150 cursor-pointer flex gap-3 ${
                  selectedId === c.id ? "bg-sv-hover" : "hover:bg-sv-hover"
                }`}
              >
                <input
                  type="checkbox"
                  data-testid={`conv-checkbox-${c.id}`}
                  checked={checked.has(c.id)}
                  onChange={(e) => toggleCheck(c.id, e)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1 cursor-pointer accent-black shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-sv-ink font-medium truncate flex items-center gap-2" data-testid={`conv-name-${c.id}`}>
                      {c.needs_attention && (
                        <span title="Needs a response" data-testid={`conv-attention-${c.id}`}
                          className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: "#F59E0B" }} />
                      )}
                      {c.display_name || c.phone}
                    </span>
                    <ClsPill cls={c.classification} confidence={c.confidence} testid={`conv-cls-${c.id}`} />
                  </div>
                  {(c.display_name || c.company) && (
                    <div className="flex items-center gap-1.5 mt-0.5 font-mono text-[11px] text-sv-muted truncate">
                      {c.display_name && <span>{c.phone}</span>}
                      {c.company && <span className="truncate">· {c.company}</span>}
                    </div>
                  )}
                  <p className="text-sv-secondary text-sm mt-1 truncate">{c.last_reply_text || "—"}</p>
                  <div className="flex items-center justify-between mt-1.5 font-mono text-[10px] text-sv-muted uppercase tracking-wider">
                    <span className="truncate max-w-[150px]">{c.campaign_name}</span>
                    <span>{fmtTime(c.last_reply_at)}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Thread */}
        <div className="border border-sv-border bg-sv-surface flex flex-col max-h-[70vh]" data-testid="conversation-thread">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center p-10">
              <p className="font-mono text-sm text-sv-muted">SELECT_A_CONVERSATION</p>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="border-b border-sv-border px-5 py-3 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-base text-sv-ink font-medium" data-testid="thread-name">
                      {thread.display_name || thread.phone}
                    </p>
                    <p className="font-mono text-[11px] text-sv-muted" data-testid="thread-phone">
                      {[thread.display_name ? thread.phone : null, thread.company].filter(Boolean).join(" · ")}
                    </p>
                    <p className="font-mono text-[10px] text-sv-muted uppercase tracking-wider">{thread.campaign_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <ClsPill cls={thread.classification} confidence={thread.confidence} testid="thread-cls" />
                    {(thread.do_not_call || thread.opt_out) && (
                      <button
                        data-testid="undo-optout-button"
                        onClick={undoOptout}
                        className="inline-flex items-center gap-1.5 h-8 px-3 font-mono text-[11px] uppercase tracking-wider border transition-colors duration-150"
                        style={{ color: "#B45309", borderColor: "#B4530955", background: "#B4530912" }}
                      >
                        <ArrowUUpLeft size={13} weight="bold" /> Undo opt-out
                      </button>
                    )}
                    <button
                      data-testid="thread-delete-button"
                      onClick={askThreadDelete}
                      title="Delete this conversation"
                      className="inline-flex items-center justify-center h-8 w-8 border border-sv-border text-sv-muted hover:text-sv-stop hover:border-sv-stop transition-colors duration-150"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
                {/* Manual classification tags */}
                <div className="flex flex-wrap items-center gap-1.5" data-testid="classify-controls">
                  <span className="font-mono text-[10px] text-sv-muted uppercase tracking-wider mr-1">Tag:</span>
                  {CLASSIFY_OPTS.map(([key, label, color]) => {
                    const active = thread.classification === key;
                    return (
                      <button
                        key={key}
                        data-testid={`classify-btn-${key}`}
                        onClick={() => setClassification(key)}
                        className="h-7 px-2.5 font-mono text-[10px] uppercase tracking-wider border transition-colors duration-150"
                        style={active
                          ? { color: "#fff", background: color, borderColor: color }
                          : { color, borderColor: color + "55", background: color + "10" }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {/* Move / re-classify dropdown */}
                <div className="flex flex-wrap items-center gap-2" data-testid="bucket-controls">
                  <span className="font-mono text-[10px] text-sv-muted uppercase tracking-wider">Move to:</span>
                  <select
                    data-testid="bucket-select"
                    value={thread.do_not_call ? "dnc" : (thread.bucket || "follow_up")}
                    onChange={(e) => onBucketSelect(e.target.value)}
                    className="h-8 px-2 bg-sv-bg border border-sv-border text-sm focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
                  >
                    {BUCKETS.map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                    <option value="dnc">Do Not Call</option>
                  </select>
                </div>
              </div>

              {(thread.do_not_call || thread.opt_out) && (
                <div
                  data-testid="dnc-banner"
                  className="px-5 py-2 font-mono text-[11px] flex items-center gap-2"
                  style={{ color: "#DC2626", background: "#DC262610", borderBottom: "1px solid #DC262633" }}
                >
                  <PhoneX size={13} weight="bold" /> Blacklisted (Do-Not-Call) — campaign stopped. Use "Undo opt-out" to reinstate.
                </div>
              )}
              {thread.classification === "hard_reject" && thread.reason && (
                <div
                  data-testid="hard-reject-reason"
                  className="px-5 py-2 font-mono text-[11px] flex items-start gap-2"
                  style={{ color: "#B45309", background: "#B4530910", borderBottom: "1px solid #B4530933" }}
                >
                  <WarningCircle size={14} weight="bold" className="mt-0.5 shrink-0" />
                  <span><b>AI reasoning:</b> {thread.reason}</span>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="thread-messages">
                {(thread.thread || []).length === 0 ? (
                  <p className="font-mono text-sm text-sv-muted text-center py-6">NO_MESSAGES</p>
                ) : (
                  (thread.thread || []).map((m, i) => {
                    const inbound = m.direction === "inbound";
                    return (
                      <div key={i} className={`flex ${inbound ? "justify-start" : "justify-end"}`} data-testid={`thread-msg-${i}`}>
                        <div className={`max-w-[78%] ${inbound ? "" : "text-right"}`}>
                          <div className="flex items-center gap-2 mb-1" style={{ justifyContent: inbound ? "flex-start" : "flex-end" }}>
                            <span
                              className="font-mono text-[10px] uppercase tracking-wider"
                              style={{ color: inbound ? "#2563EB" : "#16A34A" }}
                              data-testid={`thread-label-${i}`}
                            >
                              {m.label}
                            </span>
                            <span className="font-mono text-[10px] text-sv-muted">{fmtTime(m.at)}</span>
                          </div>
                          <div
                            className="px-3.5 py-2.5 text-sm text-left border"
                            style={inbound
                              ? { background: "#EFF6FF", borderColor: "#2563EB33", color: "#0A0A0A" }
                              : { background: "#F0FDF4", borderColor: "#16A34A33", color: "#0A0A0A" }}
                          >
                            {m.text || <span className="text-sv-muted">(empty)</span>}
                            {m.manual && (
                              <span className="ml-2 font-mono text-[9px] uppercase tracking-wider text-sv-muted">manual</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              {/* Reply box */}
              <div className="border-t border-sv-border p-4">
                {thread.opt_out ? (
                  <p className="font-mono text-[11px] text-sv-stop flex items-center gap-2" data-testid="reply-blocked">
                    <Prohibit size={14} weight="bold" /> Contact opted out. Undo opt-out to reply.
                  </p>
                ) : (
                  <div className="flex items-end gap-3">
                    <textarea
                      data-testid="manual-reply-input"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply(); }}
                      rows={2}
                      placeholder="Type a reply… (⌘/Ctrl+Enter to send)"
                      className="flex-1 bg-sv-bg border border-sv-border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
                    />
                    <button
                      data-testid="send-reply-button"
                      onClick={sendReply}
                      disabled={sending || !reply.trim()}
                      className="h-11 px-5 bg-sv-ink text-sv-onink font-semibold text-sm flex items-center gap-2 hover:bg-sv-secondary transition-colors duration-150 disabled:opacity-40"
                    >
                      <PaperPlaneTilt size={16} weight="fill" /> {sending ? "…" : "Send"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirm modal */}
      {confirm && (
        <div
          data-testid="confirm-delete-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConfirm(null)}
        >
          <div
            className="w-full max-w-md border border-sv-border bg-sv-surface p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <WarningCircle size={24} weight="fill" style={{ color: "#DC2626" }} className="shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display font-bold text-lg tracking-tight uppercase">Are you sure?</h3>
                <p className="text-sv-secondary text-sm mt-1">{confirm.message}</p>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                data-testid="confirm-delete-cancel"
                onClick={() => setConfirm(null)}
                className="h-10 px-4 border border-sv-border text-sm hover:bg-sv-hover transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-confirm"
                onClick={confirm.onConfirm}
                className="h-10 px-5 font-semibold text-sm flex items-center gap-2"
                style={{ background: "#DC2626", color: "#fff" }}
              >
                <Trash size={15} weight="bold" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
